/**
 * Clinic Store - Healthcare Context Management
 *
 * Manages clinic, organization, patient, and visit context.
 * Provides hooks for healthcare workflow state and navigation.
 */

import { useShallow } from "zustand/react/shallow";
import { createStore, withLogging } from "../../core";
import type { ClinicContext, ClinicInfo, ClinicStoreState } from "../../types/healthcare";

// Initial state
const initialState: Omit<ClinicStoreState, keyof ClinicStoreActions> = {
  organizationId: null,
  clinicId: "",
  patientId: null,
  visitId: null,
  clinicName: null,
  organizationName: null,
  isInitialized: false,
  lastInitializedAt: null,
  _initialized: false,
  _lastUpdated: Date.now(),
};

// Actions interface
interface ClinicStoreActions {
  setOrganizationId: (id: string | null) => void;
  setClinicId: (id: string | null) => void;
  setPatientId: (id: string | null) => void;
  setVisitId: (id: string | null) => void;
  setClinicInfo: (info: ClinicInfo) => void;
  setContext: (context: ClinicContext) => void;
  markAsInitialized: () => void;
  reset: () => void;
}

// Complete store state
type CompleteClinicStoreState = ClinicStoreState & ClinicStoreActions;

// Create the store
const useClinicStore = createStore<CompleteClinicStoreState>(
  withLogging("ClinicStore", (set, _get) => ({
    ...initialState,

    // Actions
    setOrganizationId: (id) =>
      set((_state) => ({
        organizationId: id,
        _lastUpdated: Date.now(),
      })),

    setClinicId: (id) =>
      set((_state) => ({
        clinicId: id,
        _lastUpdated: Date.now(),
      })),

    setPatientId: (id) =>
      set((_state) => ({
        patientId: id,
        _lastUpdated: Date.now(),
      })),

    setVisitId: (id) =>
      set((_state) => ({
        visitId: id,
        _lastUpdated: Date.now(),
      })),

    setClinicInfo: (info) =>
      set((_state) => ({
        organizationId: info.organizationId,
        clinicId: info.clinicId,
        clinicName: info.clinicName,
        organizationName: info.organizationName,
        _lastUpdated: Date.now(),
      })),

    setContext: (context) =>
      set((_state) => ({
        organizationId: context.organizationId,
        clinicId: context.clinicId,
        patientId: context.patientId,
        visitId: context.visitId,
        _lastUpdated: Date.now(),
      })),

    markAsInitialized: () =>
      set((_state) => ({
        isInitialized: true,
        lastInitializedAt: new Date(),
        _initialized: true,
        _lastUpdated: Date.now(),
      })),

    reset: () =>
      set((_state) => ({
        ...initialState,
        _lastUpdated: Date.now(),
      })),
  })),
  {
    name: "clinic-store",
    persist: true,
    version: 3, // Increment version to trigger migration
    migrate: (persistedState: unknown, version: number) => {
      // Define a type for the legacy state to avoid using 'any'
      type LegacyState = {
        organisationId?: string;
        organizationId?: string;
      };

      const state = persistedState as LegacyState;

      // Migrate from old store which used 'organisationId' at version 2
      if (version < 3 && state.organisationId) {
        state.organizationId = state.organisationId;
        delete state.organisationId;
      }
      return state as CompleteClinicStoreState;
    },
  },
);

// ===== SELECTORS =====
/**
 * Get clinic store state (simplified access)
 * @returns Complete clinic state
 */
export const useClinic = () => useClinicStore((state) => state);

// ===== DATA SELECTORS =====
/**
 * Get current clinic context (all IDs)
 */
export const useClinicContext = () =>
  useClinicStore(
    useShallow((state) => ({
      organizationId: state.organizationId,
      clinicId: state.clinicId,
      patientId: state.patientId,
      visitId: state.visitId,
    })),
  );

/**
 * Get clinic display information
 */
export const useClinicInfo = () =>
  useClinicStore(
    useShallow((state) => ({
      clinicName: state.clinicName,
      organizationName: state.organizationName,
    })),
  );

/**
 * Get organization ID
 */
export const useOrganizationId = () => useClinicStore((state) => state.organizationId);

/**
 * Get clinic ID
 */
export const useClinicId = () => useClinicStore((state) => state.clinicId);

/**
 * Get patient ID
 */
export const usePatientId = () => useClinicStore((state) => state.patientId);

/**
 * Get visit ID
 */
export const useVisitId = () => useClinicStore((state) => state.visitId);

// ===== STATE SELECTORS =====
/**
 * Get clinic initialization state
 */
export const useIsClinicInitialized = () => useClinicStore((state) => state.isInitialized);

// ===== COMPUTED SELECTORS =====
/**
 * Check if clinic is selected
 */
export const useHasClinicSelected = () =>
  useClinicStore((state) => Boolean(state.organizationId && state.clinicId));

// NOTE: useHasPatientSelected and useHasActiveVisit moved to respective domain stores

/**
 * Check if clinic context needs initialization
 */
export const useNeedsClinicInitialization = () =>
  useClinicStore((state) => !state.isInitialized || !state.organizationId || !state.clinicId);

/**
 * Get clinic display names with fallbacks
 */
export const useClinicDisplayNames = () =>
  useClinicStore(
    useShallow((state) => ({
      clinicName: state.clinicName || `Clinic ${state.clinicId}`,
      organizationName: state.organizationName || `Organization ${state.organizationId}`,
    })),
  );

// ===== ACTION HOOKS =====
/**
 * Get clinic action functions
 */
export const useClinicActions = () =>
  useClinicStore(
    useShallow((state) => ({
      setOrganizationId: state.setOrganizationId,
      setClinicId: state.setClinicId,
      setPatientId: state.setPatientId,
      setVisitId: state.setVisitId,
      setClinicInfo: state.setClinicInfo,
      setContext: state.setContext,
      markAsInitialized: state.markAsInitialized,
      reset: state.reset,
    })),
  );

// Export the store for direct access if needed
export { useClinicStore };
